package com.fantasytenis.web.components

import androidx.compose.runtime.Composable
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Skeleton loading components for improved perceived performance.
 * Provides visual placeholders that match the shape of the content being loaded.
 */

/**
 * Base skeleton component with shimmer animation.
 *
 * @param width Width of the skeleton (default: 100%)
 * @param height Height of the skeleton
 * @param borderRadius Border radius for rounded corners
 */
@Composable
fun Skeleton(
    width: CSSNumeric = 100.percent,
    height: CSSNumeric,
    borderRadius: CSSNumeric = 4.px
) {
    Div({
        classes("skeleton-shimmer")
        style {
            this.width(width)
            this.height(height)
            this.borderRadius(borderRadius)
            backgroundColor(Colors.Gray200)
            property("animation", "shimmer 1.5s ease-in-out infinite")
        }
    })
}

/**
 * Text skeleton for loading text content.
 */
@Composable
fun SkeletonText(
    width: CSSNumeric = 100.percent,
    lines: Int = 1,
    lineHeight: CSSNumeric = 16.px,
    gap: CSSNumeric = 8.px
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            this.gap(gap)
        }
    }) {
        repeat(lines) { index ->
            // Make last line shorter for natural text appearance
            val lineWidth = if (index == lines - 1 && lines > 1) 70.percent else width
            Skeleton(width = lineWidth, height = lineHeight)
        }
    }
}

/**
 * Circle skeleton for avatars or round elements.
 */
@Composable
fun SkeletonCircle(size: CSSNumeric = 40.px) {
    Skeleton(width = size, height = size, borderRadius = 50.percent)
}

/**
 * Skeleton for a contest card matching the ContestCard layout.
 */
@Composable
fun SkeletonContestCard() {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(20.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Header with badges
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(12.px)
            }
        }) {
            Skeleton(width = 48.px, height = 22.px)
            Skeleton(width = 72.px, height = 22.px)
        }

        // Title
        Skeleton(width = 80.percent, height = 24.px, borderRadius = 4.px)
        Div({ style { height(4.px) } })

        // Subtitle
        Skeleton(width = 60.percent, height = 16.px, borderRadius = 4.px)
        Div({ style { height(16.px) } })

        // Entry/Prize row
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(12.px)
            }
        }) {
            Div {
                Skeleton(width = 40.px, height = 12.px)
                Div({ style { height(4.px) } })
                Skeleton(width = 60.px, height = 20.px)
            }
            Div {
                Skeleton(width = 60.px, height = 12.px)
                Div({ style { height(4.px) } })
                Skeleton(width = 80.px, height = 20.px)
            }
        }

        // Progress bar
        Div({
            style {
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    marginBottom(4.px)
                }
            }) {
                Skeleton(width = 50.px, height = 12.px)
                Skeleton(width = 60.px, height = 12.px)
            }
            Skeleton(width = 100.percent, height = 4.px, borderRadius = 2.px)
        }

        // Chips
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                marginBottom(16.px)
            }
        }) {
            Skeleton(width = 80.px, height = 24.px, borderRadius = 12.px)
            Skeleton(width = 64.px, height = 24.px, borderRadius = 12.px)
        }

        // Button
        Skeleton(width = 100.percent, height = 44.px, borderRadius = 8.px)
    }
}

/**
 * Grid of skeleton contest cards.
 */
@Composable
fun SkeletonContestGrid(count: Int = 6) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
            gap(24.px)
        }
    }) {
        repeat(count) {
            SkeletonContestCard()
        }
    }
}

/**
 * Skeleton for leaderboard table rows.
 */
@Composable
fun SkeletonLeaderboardRow() {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "60px 1fr 100px 80px")
            padding(12.px, 16.px)
            alignItems(AlignItems.Center)
            backgroundColor(Colors.White)
            property("border-bottom", "1px solid ${Colors.BorderLight}")
        }
    }) {
        // Rank
        SkeletonCircle(36.px)

        // Player name
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
            }
        }) {
            Skeleton(width = 120.px, height = 16.px)
            Skeleton(width = 80.px, height = 12.px)
        }

        // Points
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexEnd)
            }
        }) {
            Skeleton(width = 60.px, height = 20.px)
        }

        // Change
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexEnd)
            }
        }) {
            Skeleton(width = 40.px, height = 16.px)
        }
    }
}

/**
 * Skeleton for leaderboard table.
 */
@Composable
fun SkeletonLeaderboard(rowCount: Int = 10) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            overflow("hidden")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "60px 1fr 100px 80px")
                padding(12.px, 16.px)
                backgroundColor(Colors.SurfaceVariant)
                property("border-bottom", "1px solid ${Colors.Border}")
            }
        }) {
            Skeleton(width = 40.px, height = 12.px)
            Skeleton(width = 60.px, height = 12.px)
            Skeleton(width = 50.px, height = 12.px)
            Skeleton(width = 50.px, height = 12.px)
        }

        // Rows
        repeat(rowCount) {
            SkeletonLeaderboardRow()
        }
    }
}

/**
 * Skeleton for player picker list items.
 */
@Composable
fun SkeletonPlayerRow() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(16.px)
            backgroundColor(Colors.Background)
            borderRadius(8.px)
            border(1.px, LineStyle.Solid, Colors.Border)
        }
    }) {
        // Player info
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            SkeletonCircle(40.px)
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(4.px)
                }
            }) {
                Skeleton(width = 120.px, height = 16.px)
                Skeleton(width = 80.px, height = 12.px)
            }
        }

        // Cost
        Skeleton(width = 60.px, height = 28.px, borderRadius = 4.px)
    }
}

/**
 * Skeleton for player picker list.
 */
@Composable
fun SkeletonPlayerList(count: Int = 8) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
        }
    }) {
        repeat(count) {
            SkeletonPlayerRow()
        }
    }
}
